// services/stripeService.ts
import { supabase } from '@/lib/supabase';

export class StripeService {
  /**
   * Processes a successful cart payment by creating transaction records for each photo
   */
  static async processCartPayment(paymentIntentId, cartItems) {
    try {
      if (!paymentIntentId) {
        throw new Error('Payment intent ID is required');
      }
      
      if (!cartItems || !cartItems.length) {
        throw new Error('Cart items are required');
      }
  
      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('Authentication required');
      }
  
      // Process each item in the cart
      for (const item of cartItems) {
        const photo = item.photo;
        
        // Use the price from cart item which should already have discounts applied
        const itemTotal = photo.price;  // This now contains the discounted price
        const platformFee = itemTotal * 0.15;
        const photographerAmount = itemTotal * 0.85;
  
        // Create transaction record
        const { data: transaction, error: transactionError } = await supabase
          .from('transactions')
          .insert({
            order_id: paymentIntentId,
            photo_id: photo.id,
            buyer_id: user.id,
            photographer_id: photo.user_id,
            amount: itemTotal,
            platform_fee: platformFee,
            photographer_amount: photographerAmount,
            status: 'completed',
            payment_method: 'stripe'
          })
          .select()
          .single();
  
        if (transactionError) {
          console.error('Transaction creation error:', transactionError);
          throw transactionError;
        }
  
        // Create purchase record
        const { error: purchaseError } = await supabase
          .from('purchased_photos')
          .insert({
            user_id: user.id,
            photo_id: photo.id,
            transaction_id: transaction.id,
            created_at: new Date().toISOString()
          });
  
        if (purchaseError) {
          console.error('Purchase record error:', purchaseError);
          throw purchaseError;
        }
  
        // Update photographer balance
        const { error: balanceError } = await supabase
          .from('profiles')
          .update({
            balance: supabase.rpc('increment_balance', { amount: photographerAmount })
          })
          .eq('id', photo.user_id);
  
        if (balanceError) {
          console.error('Balance update error:', balanceError);
          throw balanceError;
        }
      }
  
      return { success: true };
    } catch (error) {
      console.error('StripeService.processCartPayment error:', error);
      throw error;
    }
  }
  
  /**
   * Creates a payment intent for purchasing a photo
   * @param photoId The ID of the photo being purchased
   * @param amount The payment amount in dollars (will be converted to cents)
   * @returns Promise with the client secret
   */
  static async createPaymentIntent(photoId, amount) {
    try {
      // Validate parameters
      if (!photoId || !amount) throw new Error('Missing required parameters');
      
      // Get API URL from environment
      const functionUrl = import.meta.env.VITE_SUPABASE_FUNCTIONS_URL || 
                           'https://vbplelcgweacprllgxed.supabase.co/functions/v1';
      
      const endpoint = `${functionUrl}/create-payment-intent`;
      console.log(`Calling Stripe payment intent endpoint: ${endpoint}`);
      
      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw new Error('Authentication required');
  
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        },
        body: JSON.stringify({
          photoId,
          amount: amount, // Convert to cents
          userId: user.id
        })
      });
  
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to initialize payment');
      }
  
      const data = await response.json();
      return { 
        clientSecret: data.clientSecret,
        paymentIntentId: data.paymentIntentId
      };
    } catch (error) {
      console.error('StripeService.createPaymentIntent error:', error);
      throw error;
    }
  }
  static async createCartPaymentIntent(photos, totalAmount) {
    try {
      if (!photos || !photos.length || !totalAmount) {
        throw new Error('Missing required parameters');
      }
  
      const functionUrl = import.meta.env.VITE_SUPABASE_FUNCTIONS_URL ||
                         'https://vbplelcgweacprllgxed.supabase.co/functions/v1';
      const endpoint = `${functionUrl}/create-cart-payment-intent`;
  
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw new Error('Authentication required');
  
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        },
        body: JSON.stringify({
          photos,
          totalAmount, // This should now be the discounted total from the cart
          metadata: {
            buyerId: user.id,
          }
        })
      });
  
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to initialize payment');
      }
  
      const data = await response.json();
      return {
        clientSecret: data.clientSecret,
        paymentIntentId: data.paymentIntentId
      };
    } catch (error) {
      console.error('StripeService.createCartPaymentIntent error:', error);
      throw error;
    }
  }
  
  /**
   * Processes a successful payment by creating transaction records
   * @param paymentIntentId The Stripe payment intent ID
   * @param photoId The purchased photo ID
   * @param buyerId The buyer's user ID (optional, will use current user if empty)
   * @param photographerId The photographer's ID (optional, will determine from photo)
   * @param amount The payment amount
   * @returns Promise with the transaction data
   */
  static async processPayment(paymentIntentId, photoId, buyerId = '', photographerId = '', amount) {
    try {
      // Validate critical parameters
      if (!paymentIntentId) {
        throw new Error('Payment intent ID is required');
      }
      
      if (!photoId) {
        console.error('Missing photoId in processPayment:', photoId);
        throw new Error('Photo ID is required');
      }

      // Get current user if buyerId not provided
      let currentUserId = buyerId;
      if (!currentUserId) {
        const { data: { user }, error: userError } = await supabase.auth.getUser();
        if (userError || !user) {
          throw new Error('Authentication required');
        }
        currentUserId = user.id;
      }

      // Get photo details if photographerId not provided
      let actualPhotographerId = photographerId;
      if (!actualPhotographerId) {
        const { data: photo, error: photoError } = await supabase
          .from('photos')
          .select('user_id')
          .eq('id', photoId)  // Use photoId parameter directly
          .single();
          
        if (photoError) {
          console.error('Photo fetch error in processPayment:', photoError);
          throw new Error('Photo information unavailable');
        }
        
        actualPhotographerId = photo.user_id;
      }

      // Calculate fees
      const totalAmount = amount;
      const platformFee = totalAmount * 0.15; // 15% platform fee
      const photographerAmount = totalAmount * 0.85; // 85% to photographer

      // Create transaction record
      const { data: transaction, error: transactionError } = await supabase
        .from('transactions')
        .insert({
          order_id: paymentIntentId,
          photo_id: photoId,  // Use photoId parameter directly
          buyer_id: currentUserId,
          photographer_id: actualPhotographerId,
          amount: totalAmount,
          platform_fee: platformFee,
          photographer_amount: photographerAmount,
          status: 'completed',
          payment_method: 'stripe'
        })
        .select()
        .single();

      if (transactionError) {
        console.error('Transaction creation error:', transactionError);
        throw transactionError;
      }


      // Create purchase record
      const { error: purchaseError } = await supabase
        .from('purchased_photos')
        .insert({
          user_id: currentUserId,
          photo_id: photoId,  // Use photoId parameter directly
          id: transaction.id
        });

      if (purchaseError) {
        console.error('Purchase record error:', purchaseError);
        throw purchaseError;
      }

      return { success: true, transaction };
    } catch (error) {
      console.error('StripeService.processPayment error:', error);
      throw error;
    }
  }

  /**
   * Verifies the status of a payment
   * @param paymentIntentId The Stripe payment intent ID to verify
   * @returns Promise with the payment status information
   */
  static async verifyPaymentStatus(paymentIntentId) {
    try {
      const response = await fetch('/api/verify-payment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        },
        body: JSON.stringify({
          paymentIntentId
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to verify payment status');
      }

      return await response.json();
    } catch (error) {
      console.error('StripeService.verifyPaymentStatus error:', error);
      throw error;
    }
  }
}